import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, HourglassSimple } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtH = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('fr-FR')}h`;
const fmtD = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('fr-FR')}j`;
const MONTH_LABELS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const formatMonth = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const y = ym.slice(0, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_LABELS_FR[m - 1] || ym} ${y}`;
};

/**
 * Compact panel showing total worked days/hours since beginning + monthly breakdown.
 * Used in MyHoursPage (employee) and as a callable summary by admins for any user.
 */
export const WorkedDaysSummary = ({ userId = null, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = userId
        ? `${API_URL}/api/time-entries/worked-summary?user_id=${userId}`
        : `${API_URL}/api/time-entries/worked-summary`;
      const res = await axios.get(url, { withCredentials: true });
      setData(res.data);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return null;
  if (!data) return null;
  const { totals, monthly, day_hours } = data;

  return (
    <div data-testid="worked-days-summary" className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Synthese de mes jours travailles</h3>
        <span style={{ fontSize: '0.72rem', color: '#5E5F66' }}>Base : {day_hours}h / jour</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: monthly.length > 0 ? 14 : 0 }}>
        <div style={{ background: '#F0FDF4', padding: 12, borderRadius: 8, border: '1px solid #BBF7D0' }}>
          <div style={{ fontSize: '0.72rem', color: '#065F46', fontWeight: 600, textTransform: 'uppercase' }}>Jours valides</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#047857', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={18} weight="bold" /> {fmtD(totals.days)}
          </div>
          <div style={{ fontSize: '0.74rem', color: '#5E5F66' }}>Soit {fmtH(totals.hours)}</div>
        </div>
        <div style={{ background: '#FEF3C7', padding: 12, borderRadius: 8, border: '1px solid #FDE68A' }}>
          <div style={{ fontSize: '0.72rem', color: '#92400E', fontWeight: 600, textTransform: 'uppercase' }}>En attente</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#D97706', display: 'flex', alignItems: 'center', gap: 6 }}>
            <HourglassSimple size={18} weight="bold" /> {fmtD(totals.days_pending)}
          </div>
          <div style={{ fontSize: '0.74rem', color: '#5E5F66' }}>Soit {fmtH(totals.hours_pending)}</div>
        </div>
        <div style={{ background: '#EFF6FF', padding: 12, borderRadius: 8, border: '1px solid #BFDBFE' }}>
          <div style={{ fontSize: '0.72rem', color: '#1E3A8A', fontWeight: 600, textTransform: 'uppercase' }}>Mois actifs</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Clock size={18} weight="bold" /> {monthly.length}
          </div>
        </div>
      </div>

      {!compact && monthly.length > 0 && (
        <div style={{ overflow: 'auto', maxHeight: 360 }}>
          <table className="data-table" style={{ margin: 0, fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th>Mois</th>
                <th style={{ textAlign: 'right' }}>Jours</th>
                <th style={{ textAlign: 'right' }}>Heures</th>
                <th style={{ textAlign: 'right' }}>En attente</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map(m => (
                <tr key={m.month} data-testid={`worked-month-${m.month}`}>
                  <td style={{ fontWeight: 600 }}>{formatMonth(m.month)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#047857' }}>{fmtD(m.days)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtH(m.hours)}</td>
                  <td style={{ textAlign: 'right', color: m.hours_pending > 0 ? '#D97706' : '#9CA3AF' }}>
                    {m.hours_pending > 0 ? `${fmtD(m.days_pending)} / ${fmtH(m.hours_pending)}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkedDaysSummary;
